/* istanbul ignore file */

import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  contentChild,
  contentChildren,
  input,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';

import {
  TemplateOverrideDirective,
  findTemplateOverride,
} from '../tpl-override.directive';

interface ShowMoreTemplateContext {
  $implicit: () => void;
  expanded: boolean;
}

// Small component that shows a read more (configurable) button
// when the content starts to overflow.
// The client of this component should set one of maxLines, maxHeightClass or
// maxHeight to control the maximum allowed height of this wrapped content
@Component({
  selector: 'joymo-show-more-block',
  templateUrl: './show-more-block.component.html',
  imports: [CommonModule, TemplateOverrideDirective],
  styleUrls: ['./show-more-block.component.scss'],
  standalone: true,
})
export class ShowMoreBlockComponent {
  maxLines = input<number>();
  maxHeight = input<string | number>('');
  maxHeightClass = input<string | number>('');

  expandArea = signal<boolean>(false);

  _maxHeight = computed<string>(() => {
    const expanded = this.expandArea();
    const maxHeight = this.maxHeight();
    const maxLines = this.maxLines();
    if (expanded) {
      return '';
    }
    if (typeof maxLines === 'number') {
      const lineHeight = 1.5; // Magic constant from Vlad;
      return `calc( 1em * ${lineHeight} * ${maxLines} )`;
    }
    return typeof maxHeight === 'string' ? maxHeight : maxHeight + 'px';
  });

  _maxHeightClass = computed(() => {
    const expanded = this.expandArea();
    const maxHeightClass = this.maxHeightClass();
    return expanded ? 'force-expanded' : maxHeightClass;
  });

  overrides = contentChildren(TemplateOverrideDirective);

  buttonTpl = findTemplateOverride(this.overrides, 'showMore');

  defaultTpl =
    viewChild.required<TemplateRef<ShowMoreTemplateContext>>(
      'defaultLoadMoreTpl'
    );

  _loadMoreTpl = computed(() => {
    const providedTpl = this.buttonTpl();
    const defaultTpl = this.defaultTpl();
    return providedTpl ?? defaultTpl;
  });

  showMoreToggle() {
    this.expandArea.set(!this.expandArea());
  }
}
